import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, ArrowRight, Plus, Minus, Trash2, Loader2, ExternalLink, Gift } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useCartStore } from '@/stores/cartStore';
import { formatPrice } from '@/lib/shopify';
import { PageSeoHead } from '@/components/seo/PageSeoHead';


const Cart = () => {
  const { t, i18n } = useTranslation();
  const isIT = i18n.language !== 'en';
  const { 
    items, 
    updateQuantity, 
    removeItem, 
    toggleGiftWrap,
    refreshCheckoutUrl,
    isLoading,
    isSyncing,
    getTotalPrice,
  } = useCartStore();

  const handleCheckout = async () => {
    // Open the window immediately on user gesture to avoid iOS Safari popup blocker,
    // then navigate it once the checkout URL resolves.
    const newWindow = window.open('about:blank', '_blank');
    if (newWindow) newWindow.opener = null;

    const checkoutUrl = await refreshCheckoutUrl();
    if (checkoutUrl && newWindow) {
      newWindow.location.href = checkoutUrl;
    } else if (checkoutUrl) {
      // Popup was blocked: fall back to navigating the current tab
      window.location.href = checkoutUrl;
    } else if (newWindow) {
      newWindow.close();
    }
  };

  const totalPrice = getTotalPrice();

  return (
    <Layout>
      <PageSeoHead
        title={isIT ? 'Carrello – Sampaoli Creazioni' : 'Cart – Sampaoli Creazioni'}
        description={isIT ? 'Il tuo carrello su Sampaoli Creazioni.' : 'Your Sampaoli Creazioni shopping cart.'}
        path="/cart"
        noindex
      />
      <section className="section-padding bg-background min-h-[60vh]">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground mb-4">
              {t('nav.cart')}
            </h1>
          </motion.div>

          {items.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center py-16"
            >
              <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-6" />
              <h2 className="font-heading text-2xl text-foreground mb-4">
                {t('common.emptyCart')}
              </h2>
              <p className="font-body text-muted-foreground mb-8 max-w-md mx-auto">
                {t('collections.subtitle')}
              </p>
              <Button variant="bronze" size="lg" asChild>
                <Link to="/collections" className="gap-2">
                  {t('hero.cta')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* Cart Items */}
              <div className="space-y-4">
                {items.map((item) => {
                  const firstImage = item.product.node.images.edges[0]?.node;
                  return (
                    <div
                      key={item.variantId}
                      className="flex gap-4 p-4 bg-card rounded-lg border border-border"
                    >
                      <Link 
                        to={`/product/${item.product.node.handle}`}
                        className="flex-shrink-0"
                      >
                        <div className="w-20 h-20 md:w-24 md:h-24 rounded-md overflow-hidden bg-secondary">
                          {firstImage ? (
                            <img
                              src={firstImage.url}
                              alt={firstImage.altText || item.product.node.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                              {t('product.noImage')}
                            </div>
                          )}
                        </div>
                      </Link>

                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${item.product.node.handle}`}>
                          <h3 className="font-heading text-sm md:text-base font-medium text-foreground hover:text-bronze transition-colors line-clamp-2">
                            {item.product.node.title}
                          </h3>
                        </Link>
                        {item.variantTitle !== 'Default Title' && (
                          <p className="font-body text-xs text-muted-foreground mt-1">
                            {item.variantTitle}
                          </p>
                        )}
                        <p className="font-body text-sm text-bronze font-medium mt-2">
                          {formatPrice(item.price.amount, item.price.currencyCode)}
                        </p>
                        <label className="flex items-center gap-2 mt-2 cursor-pointer">
                          <Checkbox
                            checked={!!item.giftWrap}
                            onCheckedChange={() => toggleGiftWrap(item.variantId)}
                            disabled={isLoading}
                            className="h-4 w-4"
                          />
                          <Gift className="h-3.5 w-3.5 text-bronze" />
                          <span className="text-xs text-muted-foreground">
                            {t('cart.giftWrap')} {t('cart.giftWrapPrice')}
                          </span>
                        </label>
                      </div>

                      <div className="flex flex-col items-end justify-between">
                        <button
                          onClick={() => removeItem(item.variantId)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center"
                          aria-label={t('cart.removeItem')}
                          disabled={isLoading}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-1 border border-border rounded-md">
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-secondary transition-colors"
                            aria-label={t('cart.decreaseQty')}
                            disabled={isLoading}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-body text-sm w-6 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            className="p-2.5 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-secondary transition-colors"
                            aria-label={t('cart.increaseQty')}
                            disabled={isLoading}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Cart Summary */}
              <div className="border-t border-border pt-6 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="font-heading text-lg text-foreground">{t('common.total')}</span>
                  <span className="font-heading text-xl text-bronze font-semibold">
                    {formatPrice(totalPrice.toString(), 'EUR')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('cart.shippingAtCheckout')}
                </p>

                <Button 
                  variant="bronze" 
                  size="lg" 
                  className="w-full"
                  onClick={handleCheckout}
                  disabled={isLoading || isSyncing || items.length === 0}
                >
                  {isLoading || isSyncing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      {t('common.checkout')}
                    </>
                  )}
                </Button>

                <Link to="/shop" className="block mt-4">
                  <Button variant="outline" size="lg" className="w-full">
                    {t('common.continueShopping')}
                  </Button>
                </Link>

              </div>
            </motion.div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Cart;
